<template>
  <section class="statsheet-container">
    <div v-if="shouldShowLockedOverlay" class="locked-overlay">
      <div class="locked-modal">
        <i class="mdi mdi-lock-outline locked-icon"></i>
        <h2>This profile has not been locked by its creator.</h2>
        <p>Please try again later.</p>
      </div>
    </div>
    <template v-else>
      <statsheet-header
        v-if="statsheetUser"
        :user="statsheetUser"
        :self="Boolean(user && statsheetUser && user._id === statsheetUser._id)"
      >
      </statsheet-header>

      <div class="content-container container">
        <div v-if="isLoading" class="loading-container">
          <b-loading :is-full-page="false" :active="isLoading"></b-loading>
          <p class="loading-text">Loading your profile...</p>
        </div>

        <template v-else>
          <!-- Trust Score Meter Full Width -->
          <trust-score-meter
            v-if="statsheetUser?.circleFields?.activity_score"
            :activity-score="statsheetUser.circleFields.activity_score"
            :reviews="statsheetUser.reviews"
            :is-verified="statsheetUser.isSupervisorVerified"
          />

          <work-experience-timeline
            v-if="!statsheetUser?.isPublicView"
            :work-history="statsheetUser?.workHistory || []"
          />

          <AspirationsSection
            v-if="statsheetUser?.aspirations?.length"
            :aspirations="statsheetUser.aspirations"
            :self="Boolean(user && statsheetUser && user._id === statsheetUser._id)"
          />

          <div class="masonry-grid">
            <div
              v-if="statsheetUser?.resumeAnalysis?.summary"
              class="masonry-item"
            >
              <profile-summary
                :summary="statsheetUser.resumeAnalysis?.summary"
              />
            </div>

            <div
              v-if="statsheetUser?.resumeAnalysis?.skills || statsheetUser?.skills?.length"
              class="masonry-item"
            >
              <!-- Public view (isPublicView) sends top-level skills instead of resumeAnalysis. -->
              <skills-section
                :skills="statsheetUser.resumeAnalysis?.skills || statsheetUser.skills"
              />
            </div>

            <div
              v-if="statsheetUser?.recentSkills"
              class="masonry-item"
            >
              <recent-skills-section
                :skills="statsheetUser.recentSkills"
              />
            </div>

            <div
              v-if="statsheetUser?.resumeAnalysis?.certifications && statsheetUser?.resumeAnalysis?.certifications.length"
              class="masonry-item"
            >
              <certifications-section
                :certifications="statsheetUser.resumeAnalysis.certifications"
              />
            </div>

            <div
              v-if="statsheetUser?.resumeAnalysis"
              class="masonry-item"
            >
              <CareerAccomplishments
                :resume-analysis="statsheetUser.resumeAnalysis"
              />
            </div>

            <div
              v-if="statsheetUser?.resumeAnalysis?.workPreferences && statsheetUser?.resumeAnalysis?.workPreferences.length"
              class="masonry-item"
            >
              <work-preferences
                :work-preferences="statsheetUser.resumeAnalysis.workPreferences"
              />
            </div>

            <div
              v-if="statsheetUser?.resumeAnalysis?.teamPreferences && statsheetUser?.resumeAnalysis?.teamPreferences.length"
              class="masonry-item"
            >
              <team-preferences
                :teamPreferences="statsheetUser.resumeAnalysis.teamPreferences"
              />
            </div>

            <div
              v-if="statsheetUser?.resumeAnalysis?.softSkills && statsheetUser?.resumeAnalysis?.softSkills.length"
              class="masonry-item"
            >
              <soft-skills
                :soft-skills="statsheetUser.resumeAnalysis.softSkills"
              />
            </div>

            <div
              v-if="statsheetUser?.resumeAnalysis?.values && statsheetUser?.resumeAnalysis?.values.length"
              class="masonry-item"
            >
              <core-values
                :core-values="statsheetUser.resumeAnalysis.values"
              />
            </div>
          </div>

          <!-- Powered By Section -->
          <div class="powered-by-block">
            <div class="powered-by-label">Powered By:</div>
            <a href="https://teamwrkr.com" target="_blank" rel="noopener noreferrer">
              <img src="@/assets/teamwrkr.png" alt="Teamwrkr Logo" class="powered-by-logo" />
            </a>
            <div class="powered-by-cta">
              <span>
                Interested in learning more?
                <a href="/analyze" class="powered-by-link">Join Teamwrkr today</a>
              </span>
            </div>
          </div>
        </template>
      </div>
      <StatsheetUnlocked v-if="user && user?.isStatsheetLocked === false" />
    </template>
  </section>
</template>

<script>
import StatsheetHeader from '@/components/home/StatsheetHeader.vue';
import TrustScoreMeter from '@/components/home/TrustScoreMeter.vue';
import ProfileSummary from '@/components/home/ProfileSummary.vue';
import SkillsSection from '@/components/home/SkillsSection.vue';
import RecentSkillsSection from '@/components/home/RecentSkillsSection.vue';
import CertificationsSection from '@/components/home/CertificationsSection.vue';
import WorkExperienceTimeline from '@/components/home/WorkExperienceTimeline.vue';
import AspirationsSection from '@/components/home/AspirationsSection.vue'
import CareerAccomplishments from '@/components/home/CareerAccomplishments.vue';
import WorkPreferences from '@/components/home/WorkPreferences.vue';
import TeamPreferences from '@/components/home/TeamPreferences.vue';
import SoftSkills from '@/components/home/SoftSkills.vue';
import CoreValues from '@/components/home/CoreValues.vue';
import StatsheetUnlocked from '@/components/home/StatsheetUnlocked.vue';
import { useUserStore } from '@/stores/userStore';
import { mapState } from 'pinia'

export default {
  name: 'HomePage',
  components: {
    StatsheetHeader,
    TrustScoreMeter,
    ProfileSummary,
    SkillsSection,
    RecentSkillsSection,
    CertificationsSection,
    WorkExperienceTimeline,
    AspirationsSection,
    CareerAccomplishments,
    WorkPreferences,
    TeamPreferences,
    SoftSkills,
    CoreValues,
    StatsheetUnlocked,
  },
  data() {
    return {
      isStatsheetLocked: null,
      statsheetUser: null,
      viewerResolved: false,
    };
  },
  computed: {
    // Pull reactive state directly from Pinia
    ...mapState(useUserStore, ['user', 'loading']),
    isLoading() {
      return this.loading
    },
    shouldShowLockedOverlay() {
      if (this.isStatsheetLocked !== false || !this.statsheetUser || !this.viewerResolved) {
        return false
      }

      return !this.user || this.user._id !== this.statsheetUser._id
    },
  },
  watch: {
    '$route.params.id'(newId) {
      this.handleRouteChange(newId)
    },
  },
  created() {
    this.handleRouteChange(this.$route.params.id)
    this.initializeViewer()
  },
  methods: {
    async initializeViewer() {
      const token = this.$cookies.get('teamwrkr-token')
      if (!token) {
        this.viewerResolved = true
        return
      }

      if (this.user?._id) {
        this.viewerResolved = true
        return
      }

      try {
        const userStore = useUserStore()
        await userStore.fetchUser(token)
      } catch (error) {
        console.error('Error fetching viewer for public profile:', error)
      } finally {
        this.viewerResolved = true
      }
    },
    async getStatsheet(id) {
      const url = `${process.env.VUE_APP_API_URL}/users/statsheet/${id}`
      const token = this.$cookies.get('teamwrkr-token')
      const response = await this.axios.get(url, token ? { headers: { Authorization: token } } : {})
        // A stale/invalid login cookie gets a 401; retry once logged-out so the public view renders.
        .catch(error => (token && error?.response?.status === 401 ? this.axios.get(url) : Promise.reject(error)))
        .catch(error => {
          console.error('Error fetching profile data:', error);
        });
      if (!response?.data) {
        return;
      }
      this.statsheetUser = this.sanitizeValue(response.data);
      this.isStatsheetLocked = this.statsheetUser?.isStatsheetLocked;
    },
    handleRouteChange(id) {
      this.getStatsheet(id)
    },
    sanitizeValue(value) {
      if (value === 'null') {
        return null;
      }

      if (Array.isArray(value)) {
        return value
          .map(item => this.sanitizeValue(item))
          .filter(item => item !== null);
      }

      if (value && typeof value === 'object') {
        const sanitizedObject = {};
        Object.keys(value).forEach(key => {
          sanitizedObject[key] = this.sanitizeValue(value[key]);
        });
        return sanitizedObject;
      }

      return value;
    },
  }
};
</script>

<style lang="scss" scoped>
.statsheet-container {
  position: relative; // Add this!
  font-family: 'Nunito', sans-serif;
  background-color: $color-surface-cool;
  min-height: 100vh;
}

.locked-overlay {
  position: absolute; // Change from fixed to absolute
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.locked-modal {
  background: $color-surface;
  border-radius: 1.2rem;
  box-shadow: 0 4px 32px rgba(170, 118, 214, 0.12);
  padding: 2.5rem 2rem;
  max-width: 400px;
  width: 90vw;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.locked-icon {
  font-size: 2.5rem;
  color: $color-primary;
  margin-bottom: 1rem;
}

.locked-modal h2 {
  font-size: 1.3rem;
  font-weight: 700;
  color: $color-primary;
  margin-bottom: 0.7rem;
}

.locked-modal p {
  font-size: 1.05rem;
  color: $color-text-muted;
}

.masonry-grid {
  column-count: 2;
  column-gap: 2rem;
  margin-bottom: 2rem;
}

.masonry-item {
  break-inside: avoid;
  margin-bottom: 2rem;
  width: 100%;
}

@media screen and (max-width: 900px) {
  .masonry-grid {
    column-count: 1;
  }
}

.statsheet-container {
  font-family: 'Nunito', sans-serif;
  background-color: $color-surface-cool;
  min-height: 100vh;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;

  .loading-text {
    margin-top: 1.5rem;
    font-size: 1.1rem;
    color: $color-text-subtle;
  }
}

.content-container {
  padding-bottom: 3rem;
}

.powered-by-block {
  margin-top: 3rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.7rem;
}

.powered-by-label {
  font-size: 0.98rem;
  color: $color-text;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.powered-by-logo {
  width: 300px;
  max-width: 90vw;
  margin-bottom: 0.5rem;
  margin-top: 0.2rem;
}

.powered-by-cta {
  font-size: 0.98rem;
  color: $color-text;
  margin-top: 0.2rem;
  margin-bottom: 1.5rem;
  a.powered-by-link {
    color: $color-primary;
    font-weight: 700;
    text-decoration: underline;
    margin-left: 0.3rem;
    transition: color 0.18s;
    &:hover {
      color: $color-accent;
    }
  }
}
</style>
